import React from 'react';
import { motion } from 'framer-motion';
import {ShoppingCart, ExternalLink, Star, Target, Info} from 'lucide-react';

const Resources: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-900 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Mes <span className="text-blue-500">Ressources</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Les produits et suppléments que j'utilise personnellement pour mes performances
          </p>
        </motion.div>

        {/* Section Recommandations */}
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-blue-600 to-blue-800 p-8 rounded-lg mb-8 text-center"
          >
            <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-white" />
            <h2 className="text-3xl font-bold text-white mb-3">
              Mes Recommandations <span className="text-yellow-300">Personnelles</span>
            </h2>
            <p className="text-white text-lg mb-2">
              Les produits que j'utilise au quotidien pour mes performances
            </p>
            <p className="text-blue-200 text-sm">
              💪 Testés et approuvés par TitanGryx - 10 ans d'expérience
            </p>
          </motion.div>

          <div className="space-y-6">
            {/* Carte Produit Principal */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gray-800 rounded-lg p-8 border-2 border-blue-500 hover:border-yellow-400 transition-all"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="bg-blue-600 p-4 rounded-lg">
                  <Star className="w-8 h-8 text-yellow-300" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    🏆 Ma Stack Complète
                  </h3>
                  <p className="text-gray-300 mb-4">
                    Tous les suppléments essentiels que j'utilise personnellement pour optimiser mes performances, ma récupération et ma santé globale.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold">Whey Premium</span>
                    <span className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold">Créatine Pure</span>
                    <span className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold">Oméga 3</span>
                    <span className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold">Vitamines</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-700 p-6 rounded-lg mb-6">
                <h4 className="text-white font-bold mb-3 flex items-center">
                  <Target className="w-5 h-5 mr-2 text-green-400" />
                  Pourquoi je recommande cette gamme ?
                </h4>
                <ul className="text-gray-300 space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="text-green-400 mr-2">✓</span>
                    <span><strong>Qualité Contrôlée :</strong> Tests laboratoire indépendants, certifications officielles</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-400 mr-2">✓</span>
                    <span><strong>Rapport Qualité/Prix :</strong> Efficacité maximale sans se ruiner</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-400 mr-2">✓</span>
                    <span><strong>Résultats Prouvés :</strong> 10 ans d'utilisation personnelle et résultats concrets</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-400 mr-2">✓</span>
                    <span><strong>100% Légal :</strong> Aucun produit dopant, conformité totale aux normes</span>
                  </li>
                </ul>
              </div>

              <div className="space-y-3">
                <h4 className="text-white font-bold text-center mb-4">🛒 Liens Directs par Produit</h4>
                
                {/* Whey */}
                <a
                  href="https://amzn.to/3YM7gTW"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold py-3 px-4 rounded-lg hover:from-blue-400 hover:to-blue-500 transition-all transform hover:scale-105"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-lg">🥤 Whey Protéine</span>
                    <ExternalLink className="w-5 h-5" />
                  </div>
                </a>

                {/* Créatine */}
                <a
                  href="https://amzn.to/4jbN9YK"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gradient-to-r from-green-500 to-green-600 text-white font-bold py-3 px-4 rounded-lg hover:from-green-400 hover:to-green-500 transition-all transform hover:scale-105"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-lg">💊 Créatine Monohydrate</span>
                    <ExternalLink className="w-5 h-5" />
                  </div>
                </a>

                {/* Magnésium */}
                <a
                  href="https://amzn.to/3NkapaY"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white font-bold py-3 px-4 rounded-lg hover:from-purple-400 hover:to-purple-500 transition-all transform hover:scale-105"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-lg">⚡ Magnésium Bisglycinate</span>
                    <ExternalLink className="w-5 h-5" />
                  </div>
                </a>

                {/* Oméga 3 */}
                <a
                  href="https://amzn.to/4pSaQrv"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-bold py-3 px-4 rounded-lg hover:from-cyan-400 hover:to-cyan-500 transition-all transform hover:scale-105"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-lg">🐟 Oméga 3 (EPA/DHA)</span>
                    <ExternalLink className="w-5 h-5" />
                  </div>
                </a>

                {/* 23 Vitamines */}
                <a
                  href="https://amzn.to/4azf0zW"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-bold py-3 px-4 rounded-lg hover:from-yellow-400 hover:to-yellow-500 transition-all transform hover:scale-105"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-lg">☀️ 23 Vitamines Complètes</span>
                    <ExternalLink className="w-5 h-5" />
                  </div>
                </a>

                {/* L-carnitine */}
                <a
                  href="https://amzn.to/4q7tnAj"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-3 px-4 rounded-lg hover:from-orange-400 hover:to-orange-500 transition-all transform hover:scale-105"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-lg">🔥 L-Carnitine</span>
                    <ExternalLink className="w-5 h-5" />
                  </div>
                </a>

                {/* Peptides de Collagène */}
                <a
                  href="https://amzn.to/3LhaSdz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gradient-to-r from-pink-500 to-pink-600 text-white font-bold py-3 px-4 rounded-lg hover:from-pink-400 hover:to-pink-500 transition-all transform hover:scale-105"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-lg">💪 Peptides de Collagène</span>
                    <ExternalLink className="w-5 h-5" />
                  </div>
                </a>

                {/* Glutamine */}
                <a
                  href="https://amzn.to/4pXH1pu"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gradient-to-r from-teal-500 to-teal-600 text-white font-bold py-3 px-4 rounded-lg hover:from-teal-400 hover:to-teal-500 transition-all transform hover:scale-105"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-lg">🧬 Glutamine en Poudre</span>
                    <ExternalLink className="w-5 h-5" />
                  </div>
                </a>

                {/* Ashwagandha */}
                <a
                  href="https://amzn.to/3L5A9a"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gradient-to-r from-indigo-500 to-indigo-600 text-white font-bold py-3 px-4 rounded-lg hover:from-indigo-400 hover:to-indigo-500 transition-all transform hover:scale-105"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-lg">🌿 Ashwagandha Bio KSM-66</span>
                    <ExternalLink className="w-5 h-5" />
                  </div>
                </a>

                <p className="text-center text-gray-400 text-sm mt-4">
                  ⚡ Livraison rapide Amazon • Prix compétitifs
                </p>
              </div>
            </motion.div>

            {/* Section Transparence */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-gray-800 rounded-lg p-6 border-l-4 border-yellow-500"
            >
              <h4 className="text-white font-bold mb-3 flex items-center">
                <Info className="w-5 h-5 mr-2 text-yellow-400" />
                Transparence Totale
              </h4>
              <p className="text-gray-300 text-sm mt-3">
                💡 <strong>Mon engagement :</strong> Je ne recommande QUE les produits que j'utilise personnellement et en lesquels j'ai confiance à 100%. Votre santé et vos résultats passent avant tout.
              </p>
            </motion.div>

            {/* Conseils d'utilisation */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-gradient-to-r from-blue-700 to-blue-900 rounded-lg p-6"
            >
              <h4 className="text-white font-bold mb-4 text-lg">📋 Mes Conseils d'Utilisation</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-black bg-opacity-30 p-4 rounded-lg">
                  <h5 className="text-blue-300 font-semibold mb-2">🥤 Whey</h5>
                  <p className="text-white text-sm">30-40g post-training dans 250ml d'eau froide. Aussi au réveil si besoin.</p>
                </div>
                <div className="bg-black bg-opacity-30 p-4 rounded-lg">
                  <h5 className="text-blue-300 font-semibold mb-2">💊 Créatine</h5>
                  <p className="text-white text-sm">5g par jour, tous les jours, peu importe l'heure. Patience = résultats.</p>
                </div>
                <div className="bg-black bg-opacity-30 p-4 rounded-lg">
                  <h5 className="text-blue-300 font-semibold mb-2">🐟 Oméga 3</h5>
                  <p className="text-white text-sm">2-3g par jour avec un repas contenant des graisses pour absorption optimale.</p>
                </div>
                <div className="bg-black bg-opacity-30 p-4 rounded-lg">
                  <h5 className="text-blue-300 font-semibold mb-2">☀️ Vitamines</h5>
                  <p className="text-white text-sm">Matin avec le petit-déjeuner. Compense les carences alimentaires.</p>
                </div>
              </div>
            </motion.div>

            {/* Contact */}
            <div className="text-center text-gray-400 text-sm">
              <p>💬 Des questions sur les produits ou leur utilisation ? En tant que Partenaire Amazon, je réalise un bénéfice sur les achats remplissant les conditions requises.</p>
              <p className="text-blue-400 font-semibold mt-1">📧 bodytitan64@yahoo.com</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Resources;